import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Mark, mergeAttributes } from '@tiptap/core';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Highlighter,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
  Quote, Code, Minus, Link as LinkIcon, Image as ImageIcon,
  Undo2, Redo2, Table as TableIcon, Save, History, MessageSquare,
  ChevronRight, Check, X, Eye, Settings, CheckCheck, XCircle
} from 'lucide-react';

/* ── Custom Track Changes Marks ─────────────────────────────── */

const STAGE_COLORS = [
  { bg: '#dcfce7', border: '#16a34a', text: '#166534', delBg: '#dcfce750', dot: '#16a34a' },  // Stage 1: Green
  { bg: '#dbeafe', border: '#2563eb', text: '#1e40af', delBg: '#dbeafe50', dot: '#2563eb' },  // Stage 2: Blue
  { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', delBg: '#fee2e250', dot: '#dc2626' },  // Stage 3: Red
  { bg: '#fef3c7', border: '#d97706', text: '#92400e', delBg: '#fef3c750', dot: '#d97706' },  // Stage 4: Amber
  { bg: '#f3e8ff', border: '#9333ea', text: '#6b21a8', delBg: '#f3e8ff50', dot: '#9333ea' },  // Stage 5: Purple
];
const DEFAULT_STAGE_COLOR = { bg: '#f1f5f9', border: '#64748b', text: '#334155', delBg: '#f1f5f950', dot: '#64748b' };

// Mapping is done dynamically based on stage order from contractData
let _stageColorMap = {};

function buildStageColorMap(dynamicStages) {
  const map = {};
  (dynamicStages || []).forEach((s, i) => {
    map[s.id] = { ...STAGE_COLORS[i % STAGE_COLORS.length], label: s.label };
  });
  _stageColorMap = map;
}

function getStageColor(stageId) {
  if (!stageId) return { ...DEFAULT_STAGE_COLOR, label: 'Unknown' };
  if (_stageColorMap[stageId]) return _stageColorMap[stageId];
  return { ...DEFAULT_STAGE_COLOR, label: stageId };
}

const TrackInsert = Mark.create({
  name: 'trackInsert',
  addAttributes() {
    return {
      author: { default: null },
      date: { default: null },
      stage: { default: null },
      tcStyle: { default: null },
      tcTitle: { default: null },
    };
  },
  parseHTML() {
    return [{
      tag: 'ins[data-author]',
      getAttrs: el => ({
        author: el.getAttribute('data-author'),
        date: el.getAttribute('data-date'),
        stage: el.getAttribute('data-stage'),
        tcStyle: el.getAttribute('style') || null,
        tcTitle: el.getAttribute('title') || null,
      }),
    }];
  },
  renderHTML({ HTMLAttributes }) {
    // Use stored style if available, otherwise compute from stage color map
    let style = HTMLAttributes.tcStyle;
    let title = HTMLAttributes.tcTitle;
    if (!style) {
      const sc = getStageColor(HTMLAttributes.stage);
      style = `background:${sc.bg}; color:${sc.text}; text-decoration:none; border-bottom:2px solid ${sc.border}; padding:0 1px; cursor:help;`;
      title = `Added by ${HTMLAttributes.author} (${sc.label})`;
    }
    return ['ins', mergeAttributes({
      'data-author': HTMLAttributes.author,
      'data-date': HTMLAttributes.date,
      'data-stage': HTMLAttributes.stage,
      title,
      style,
    }), 0];
  },
});

const TrackDelete = Mark.create({
  name: 'trackDelete',
  addAttributes() {
    return {
      author: { default: null },
      date: { default: null },
      stage: { default: null },
      tcStyle: { default: null },
      tcTitle: { default: null },
    };
  },
  parseHTML() {
    return [{
      tag: 'del[data-author]',
      getAttrs: el => ({
        author: el.getAttribute('data-author'),
        date: el.getAttribute('data-date'),
        stage: el.getAttribute('data-stage'),
        tcStyle: el.getAttribute('style') || null,
        tcTitle: el.getAttribute('title') || null,
      }),
    }];
  },
  renderHTML({ HTMLAttributes }) {
    let style = HTMLAttributes.tcStyle;
    let title = HTMLAttributes.tcTitle;
    if (!style) {
      const sc = getStageColor(HTMLAttributes.stage);
      style = `background:${sc.delBg}; color:${sc.text}; text-decoration:line-through; padding:0 1px; cursor:help;`;
      title = `Deleted by ${HTMLAttributes.author} (${sc.label})`;
    }
    return ['del', mergeAttributes({
      'data-author': HTMLAttributes.author,
      'data-date': HTMLAttributes.date,
      'data-stage': HTMLAttributes.stage,
      title,
      style,
    }), 0];
  },
});

/* ── Toolbar helpers ─────────────────────────────────────────── */

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={title}
    className={`p-1.5 rounded transition-colors ${active ? 'bg-primary text-white' : 'hover:bg-accent text-foreground disabled:opacity-40'}`}>
    {children}
  </button>
);
const Divider = () => <div className="w-px h-5 bg-border mx-0.5" />;

/* ── CSS injected once ───────────────────────────────────────── */

const TC_STYLES = `
  .ProseMirror ins[data-author] { cursor: help; }
  .ProseMirror del[data-author] { cursor: help; }
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.textContent = TC_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
}

/* ── Main Editor ─────────────────────────────────────────────── */

export default function ContractEditor({ content, onChange, readOnly = false, contractNumber, contractData }) {
  const { user, systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const authorName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'Unknown';

  // Track changes is ON once the contract has ever left DRAFT (submitted for any review)
  const wfStatus = contractData?.workflow_status || 'DRAFT';
  const rvStage = contractData?.review_stage || 'DRAFT';
  const trackChangesActive = wfStatus !== 'DRAFT' || (rvStage !== 'DRAFT' && rvStage !== '');
  const trackRef = useRef(trackChangesActive);
  trackRef.current = trackChangesActive;
  const authorRef = useRef(authorName);
  authorRef.current = authorName;
  const stageRef = useRef(rvStage);
  stageRef.current = rvStage;

  const [showVersions, setShowVersions] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [compareVersion, setCompareVersion] = useState(null);
  const [reviewStage, setReviewStage] = useState('DRAFT');
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [stageNotes, setStageNotes] = useState('');
  const [newComment, setNewComment] = useState('');
  const [saveLabel, setSaveLabel] = useState('');
  const [stageGroups, setStageGroups] = useState({});
  const [availableGroups, setAvailableGroups] = useState([]);
  const [showStageConfig, setShowStageConfig] = useState(false);
  const [dynamicStages, setDynamicStages] = useState([]);
  const [contractStageGroups, setContractStageGroups] = useState({});
  const [showVarPicker, setShowVarPicker] = useState(false);
  const varRef = useRef(null);

  useEffect(() => { injectStyles(); }, []);

  // Helpers to create marks with styles baked in
  const makeInsertAttrs = (author, stage, now) => {
    const sc = getStageColor(stage);
    return {
      author, date: now, stage,
      tcStyle: `background:${sc.bg}; color:${sc.text}; text-decoration:none; border-bottom:2px solid ${sc.border}; padding:0 1px; cursor:help;`,
      tcTitle: `Added by ${author} (${sc.label})`,
    };
  };
  const makeDeleteAttrs = (author, stage, now) => {
    const sc = getStageColor(stage);
    return {
      author, date: now, stage,
      tcStyle: `background:${sc.delBg}; color:${sc.text}; text-decoration:line-through; padding:0 1px; cursor:help;`,
      tcTitle: `Deleted by ${author} (${sc.label})`,
    };
  };
  const insRef = useRef(makeInsertAttrs);
  insRef.current = makeInsertAttrs;
  const delRef = useRef(makeDeleteAttrs);
  delRef.current = makeDeleteAttrs;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Image.configure({ inline: true, allowBase64: true }),
      Link.configure({ openOnClick: false }),
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Placeholder.configure({ placeholder: 'Start writing your contract here...' }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
      TrackInsert,
      TrackDelete,
    ],
    content: content || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => { onChange?.(editor.getHTML()); },
    editorProps: {
      handleKeyDown: (view, event) => {
        if (readOnly || !trackRef.current) return false;
        const { state } = view;
        const author = authorRef.current;
        const stage = stageRef.current;
        const now = new Date().toISOString().slice(0, 16);

        // Typing new characters — mark as insertion
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          const { from, to } = state.selection;
          if (from !== to) {
            const tr = state.tr;
            tr.addMark(from, to, state.schema.marks.trackDelete.create(delRef.current(author, stage, now)));
            view.dispatch(tr);
          }
          setTimeout(() => {
            try {
              const { from: f } = view.state.selection;
              if (f > 0) {
                const tr = view.state.tr;
                tr.addMark(f - 1, f, view.state.schema.marks.trackInsert.create(insRef.current(author, stage, now)));
                view.dispatch(tr);
              }
            } catch {}
          }, 10);
          return false;
        }

        // Backspace/Delete — mark as deletion instead of removing
        if (event.key === 'Backspace' || event.key === 'Delete') {
          const { from, to } = state.selection;

          // Selection delete — mark the selected range as deleted
          if (from !== to) {
            event.preventDefault();
            const tr = state.tr;
            tr.addMark(from, to, state.schema.marks.trackDelete.create(delRef.current(author, stage, now)));
            tr.setSelection(state.selection.constructor.create(tr.doc, to));
            view.dispatch(tr);
            return true;
          }

          // Single char backspace
          if (event.key === 'Backspace' && from > 0) {
            // Get the character before the cursor by looking at the text node
            const $pos = state.doc.resolve(from);
            const nodeBefore = $pos.nodeBefore;

            if (nodeBefore && nodeBefore.isText) {
              const marks = nodeBefore.marks;
              const hasTrackDelete = marks.some(m => m.type.name === 'trackDelete');
              const hasTrackInsert = marks.some(m => m.type.name === 'trackInsert');

              if (hasTrackDelete) {
                // Already deleted — skip over it (move cursor left)
                event.preventDefault();
                const tr = state.tr;
                tr.setSelection(state.selection.constructor.create(tr.doc, from - 1));
                view.dispatch(tr);
                return true;
              }

              if (hasTrackInsert) {
                // Was a tracked insertion — actually delete it (undo the insert)
                return false;
              }
            }

            // Normal original text — mark as deleted, don't actually remove
            event.preventDefault();
            const tr = state.tr;
            tr.addMark(from - 1, from, state.schema.marks.trackDelete.create(delRef.current(author, stage, now)));
            tr.setSelection(state.selection.constructor.create(tr.doc, from - 1));
            view.dispatch(tr);
            return true;
          }

          // Single char forward delete
          if (event.key === 'Delete') {
            const $pos = state.doc.resolve(from);
            const nodeAfter = $pos.nodeAfter;

            if (nodeAfter && nodeAfter.isText) {
              const marks = nodeAfter.marks;
              const hasTrackDelete = marks.some(m => m.type.name === 'trackDelete');
              const hasTrackInsert = marks.some(m => m.type.name === 'trackInsert');

              if (hasTrackDelete) {
                // Already deleted — skip over it (move cursor right)
                event.preventDefault();
                const tr = state.tr;
                tr.setSelection(state.selection.constructor.create(tr.doc, from + 1));
                view.dispatch(tr);
                return true;
              }

              if (hasTrackInsert) {
                // Was a tracked insertion — actually delete it
                return false;
              }

              // Normal text — mark as deleted
              event.preventDefault();
              const tr = state.tr;
              tr.addMark(from, from + 1, state.schema.marks.trackDelete.create(delRef.current(author, stage, now)));
              view.dispatch(tr);
              return true;
            }
          }
        }

        return false;
      },
      handlePaste: (view, event) => {
        if (readOnly || !trackRef.current) return false;
        const author = authorRef.current;
        const stage = stageRef.current;
        const now = new Date().toISOString().slice(0, 16);
        const { state } = view;
        const { from, to } = state.selection;

        // If there's a selection, mark it as deleted first
        if (from !== to) {
          const tr = state.tr;
          tr.addMark(from, to, state.schema.marks.trackDelete.create(delRef.current(author, stage, now)));
          view.dispatch(tr);
        }

        // Get the paste position, let default paste happen, then mark the pasted range
        const beforePos = view.state.selection.from;
        setTimeout(() => {
          try {
            const afterPos = view.state.selection.from;
            if (afterPos > beforePos) {
              const tr = view.state.tr;
              tr.addMark(beforePos, afterPos, view.state.schema.marks.trackInsert.create(insRef.current(author, stage, now)));
              view.dispatch(tr);
            }
          } catch {}
        }, 20);
        return false; // Let default paste happen
      },
    },
  });

  // Sync editor content when content prop changes externally
  useEffect(() => {
    if (editor && content !== undefined && editor.getHTML() !== content) {
      editor.commands.setContent(content || '', false);
    }
  }, [content, editor]);

  // Load review stage, versions, comments, stage groups
  useEffect(() => {
    if (!contractNumber) return;
    api.get(`/api/contracts/${contractNumber}/review`).then(r => {
      setReviewStage(r.data.review_stage || 'DRAFT');
      setContractStageGroups(r.data.contract_stage_groups || {});
    }).catch(() => {});
    api.get('/api/contracts/admin-settings/stage-groups').then(r => {
      setStageGroups(r.data.stage_groups || {});
      setAvailableGroups(r.data.available_groups || []);
      setDynamicStages(r.data.review_stages || []);
      buildStageColorMap(r.data.review_stages || []);
    }).catch(() => {});
  }, [contractNumber]);

  const allStages = [
    { id: 'DRAFT', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
    ...dynamicStages.map(s => ({ id: s.id, label: s.label, color: 'bg-blue-100 text-blue-700' })),
    { id: 'EXECUTED', label: 'Executed', color: 'bg-green-100 text-green-700' },
  ];

  const getStageGroup = (stageId) => {
    const contractGroup = contractStageGroups[stageId];
    if (contractGroup?.group_code) return contractGroup;
    return stageGroups[stageId] || null;
  };

  const loadVersions = () => {
    if (!contractNumber) return;
    api.get(`/api/contracts/${contractNumber}/versions`).then(r => setVersions(r.data.versions || [])).catch(() => {});
  };

  const loadComments = () => {
    if (!contractNumber) return;
    api.get(`/api/contracts/${contractNumber}/comments`).then(r => setComments(r.data.comments || [])).catch(() => {});
  };

  const saveVersion = async () => {
    if (!contractNumber) return;
    await api.put(`/api/contracts/${contractNumber}/body`, { body: editor?.getHTML() || '' });
    const r = await api.post(`/api/contracts/${contractNumber}/versions`, { label: saveLabel || undefined });
    toast.success(r.data.message);
    setSaveLabel('');
    loadVersions();
  };

  const changeStage = async (newStage) => {
    try {
      await api.put(`/api/contracts/${contractNumber}/body`, { body: editor?.getHTML() || '' });
      const r = await api.put(`/api/contracts/${contractNumber}/review/stage`, { stage: newStage, notes: stageNotes });
      toast.success(r.data.message);
      setReviewStage(newStage);
      setShowStageDialog(false);
      setStageNotes('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addComment = async () => {
    if (!newComment.trim() || !contractNumber) return;
    const sel = editor?.state?.selection;
    const selText = sel && !sel.empty ? editor.state.doc.textBetween(sel.from, sel.to) : '';
    try {
      const r = await api.post(`/api/contracts/${contractNumber}/comments`, { text: newComment, selection_text: selText });
      setComments(prev => [r.data, ...prev]);
      setNewComment('');
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
  };

  const resolveComment = async (commentId) => {
    try {
      await api.put(`/api/contracts/${contractNumber}/comments/${commentId}/resolve`);
      setComments(prev => prev.map(c => c.comment_id === commentId ? { ...c, resolved: true } : c));
    } catch {}
  };

  /* ── Accept / Reject all track changes ── */
  const acceptAllChanges = () => {
    if (!editor) return;
    const { doc, schema, tr } = editor.state;
    let offset = 0;
    const deletions = []; // positions to remove (del marks)

    // First pass: collect del-marked ranges to remove
    doc.descendants((node, pos) => {
      if (node.isText) {
        const hasDel = node.marks.some(m => m.type.name === 'trackDelete');
        const hasIns = node.marks.some(m => m.type.name === 'trackInsert');
        if (hasDel) {
          deletions.push({ from: pos, to: pos + node.nodeSize });
        }
      }
    });

    // Remove deletions from end to start to maintain positions
    const transaction = editor.state.tr;
    for (let i = deletions.length - 1; i >= 0; i--) {
      transaction.delete(deletions[i].from, deletions[i].to);
    }

    // Then remove all trackInsert marks (accepted text becomes normal)
    transaction.removeMark(0, transaction.doc.content.size, schema.marks.trackInsert);

    editor.view.dispatch(transaction);
    onChange?.(editor.getHTML());
    toast.success('All changes accepted');
  };

  const rejectAllChanges = () => {
    if (!editor) return;
    const { doc, schema } = editor.state;
    const insertions = [];

    // Collect ins-marked ranges to remove
    doc.descendants((node, pos) => {
      if (node.isText && node.marks.some(m => m.type.name === 'trackInsert')) {
        insertions.push({ from: pos, to: pos + node.nodeSize });
      }
    });

    const transaction = editor.state.tr;
    // Remove insertions from end to start
    for (let i = insertions.length - 1; i >= 0; i--) {
      transaction.delete(insertions[i].from, insertions[i].to);
    }

    // Remove all trackDelete marks (restored text becomes normal)
    transaction.removeMark(0, transaction.doc.content.size, schema.marks.trackDelete);

    editor.view.dispatch(transaction);
    onChange?.(editor.getHTML());
    toast.success('All changes rejected');
  };

  // Count tracked changes and collect stages
  const countChanges = () => {
    if (!editor) return { ins: 0, del: 0, stages: {} };
    let ins = 0, del = 0;
    const stages = {};
    editor.state.doc.descendants((node) => {
      if (node.isText) {
        for (const mark of node.marks) {
          if (mark.type.name === 'trackInsert') {
            ins++;
            const s = mark.attrs.stage || 'unknown';
            const a = mark.attrs.author || 'Unknown';
            if (!stages[s]) stages[s] = { ins: 0, del: 0, authors: new Set() };
            stages[s].ins++;
            stages[s].authors.add(a);
          }
          if (mark.type.name === 'trackDelete') {
            del++;
            const s = mark.attrs.stage || 'unknown';
            const a = mark.attrs.author || 'Unknown';
            if (!stages[s]) stages[s] = { ins: 0, del: 0, authors: new Set() };
            stages[s].del++;
            stages[s].authors.add(a);
          }
        }
      }
    });
    return { ins, del, stages };
  };

  /* ── Individual change accept/reject via click ── */
  const [changePopup, setChangePopup] = useState(null);

  useEffect(() => {
    if (!editor || readOnly) return;
    const handleClick = (event) => {
      const target = event.target;
      // Check if clicked on an <ins> or <del> with data-author
      const tracked = target.closest('ins[data-author], del[data-author]');
      if (!tracked) { setChangePopup(null); return; }

      const isInsert = tracked.tagName === 'INS';
      const author = tracked.getAttribute('data-author') || 'Unknown';
      const stage = tracked.getAttribute('data-stage') || '';
      const sc = getStageColor(stage);
      const text = tracked.textContent.slice(0, 40) + (tracked.textContent.length > 40 ? '...' : '');
      const rect = tracked.getBoundingClientRect();

      setChangePopup({
        isInsert, author, stage, stageLabel: sc.label, text,
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        element: tracked,
      });
    };
    const editorEl = editor.view.dom;
    editorEl.addEventListener('click', handleClick);
    return () => editorEl.removeEventListener('click', handleClick);
  }, [editor, readOnly]);

  const acceptSingleChange = () => {
    if (!changePopup || !editor) return;
    const { element, isInsert } = changePopup;
    // Find the position of this element in the ProseMirror doc
    const pos = editor.view.posAtDOM(element, 0);
    const end = pos + element.textContent.length;
    const tr = editor.state.tr;
    if (isInsert) {
      // Accept insertion: remove the trackInsert mark (text stays)
      tr.removeMark(pos, end, editor.state.schema.marks.trackInsert);
    } else {
      // Accept deletion: actually delete the text
      tr.delete(pos, end);
    }
    editor.view.dispatch(tr);
    onChange?.(editor.getHTML());
    setChangePopup(null);
  };

  const rejectSingleChange = () => {
    if (!changePopup || !editor) return;
    const { element, isInsert } = changePopup;
    const pos = editor.view.posAtDOM(element, 0);
    const end = pos + element.textContent.length;
    const tr = editor.state.tr;
    if (isInsert) {
      // Reject insertion: delete the inserted text
      tr.delete(pos, end);
    } else {
      // Reject deletion: remove the trackDelete mark (text is restored)
      tr.removeMark(pos, end, editor.state.schema.marks.trackDelete);
    }
    editor.view.dispatch(tr);
    onChange?.(editor.getHTML());
    setChangePopup(null);
  };

  const CONTRACT_VARIABLES = [
    { var: '{{contract_number}}', label: 'Contract Number' },
    { var: '{{description}}', label: 'Description' },
    { var: '{{supplier_code}}', label: 'Supplier Code' },
    { var: '{{supplier_name}}', label: 'Supplier Name' },
    { var: '{{contract_value}}', label: 'Contract Value' },
    { var: '{{currency}}', label: 'Currency' },
    { var: '{{contract_manager}}', label: 'Contract Manager' },
    { var: '{{contract_start}}', label: 'Contract Start Date' },
    { var: '{{contract_end}}', label: 'Contract End Date' },
    { var: '{{review_date}}', label: 'Review Date' },
    { var: '{{notice_period}}', label: 'Notice Period (days)' },
    { var: '{{payment_terms}}', label: 'Payment Terms' },
    { var: '{{governing_law}}', label: 'Governing Law' },
    { var: '{{contract_renewable}}', label: 'Contract Renewable (Yes/No)' },
    { var: '{{effective_date_type}}', label: 'Effective Date Type' },
    { var: '{{effective_date}}', label: 'Effective Date' },
    { var: '{{tax_terms}}', label: 'Tax Terms' },
    { var: '{{company_name}}', label: 'Company Name' },
    { var: '{{today}}', label: "Today's Date" },
  ];

  const insertVariable = (v) => {
    if (!editor) return;
    const d = contractData || {};
    const resolvers = {
      '{{contract_number}}': d.contract_number, '{{description}}': d.description,
      '{{supplier_code}}': d.supplier_code, '{{supplier_name}}': d.supplier_name,
      '{{contract_value}}': d.contract_value ? Number(d.contract_value).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '',
      '{{currency}}': d.currency, '{{contract_manager}}': d.contract_manager,
      '{{contract_start}}': d.contract_start ? new Date(d.contract_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '',
      '{{contract_end}}': d.contract_end ? new Date(d.contract_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '',
      '{{review_date}}': d.review_date ? new Date(d.review_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '',
      '{{notice_period}}': d.notice_period ? String(d.notice_period) : '', '{{payment_terms}}': d.payment_terms,
      '{{governing_law}}': d.governing_law,
      '{{contract_renewable}}': d.contract_renewable === 'yes' ? 'Yes' : d.contract_renewable === 'no' ? 'No' : '',
      '{{effective_date_type}}': d.effective_date_type === 'on_execution' ? 'On Execution' : d.effective_date_type === 'on_specific_date' ? 'On Specific Date' : '',
      '{{effective_date}}': d.effective_date_type === 'on_execution' ? 'On Execution' : d.contract_start ? new Date(d.contract_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '',
      '{{tax_terms}}': d.tax_terms === 'all_taxes_included' ? 'All Taxes Included' : d.tax_terms === 'as_per_payment_terms' ? 'As Per Payment Terms' : '',
      '{{company_name}}': '', '{{today}}': new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }),
    };
    const resolved = resolvers[v.var];
    editor.chain().focus().insertContent(resolved && String(resolved).trim() ? String(resolved) : v.var).run();
    setShowVarPicker(false);
  };

  useEffect(() => {
    const h = e => { if (varRef.current && !varRef.current.contains(e.target)) setShowVarPicker(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const addImage = useCallback(() => {
    const url = window.prompt('Enter image URL:');
    if (url && editor) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const setLinkFn = useCallback(() => {
    const url = window.prompt('Enter URL:');
    if (url === null) return;
    if (url === '') { editor?.chain().focus().unsetLink().run(); return; }
    editor?.chain().focus().setLink({ href: url }).run();
  }, [editor]);

  const currentStageDef = allStages.find(s => s.id === reviewStage) || allStages[0];
  const currentStageIdx = allStages.findIndex(s => s.id === reviewStage);
  const changes = countChanges();
  const hasChanges = changes.ins > 0 || changes.del > 0;

  if (!editor) return null;

  return (
    <div className="border rounded-xl overflow-hidden">
      {/* Review Stage Bar */}
      {contractNumber && (
        <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Stage:</span>
            <Badge className={`${currentStageDef.color} text-xs`}>{currentStageDef.label}</Badge>
            <div className="flex items-center gap-1 ml-2">
              {allStages.map((s, i) => (
                <div key={s.id} className={`w-2 h-2 rounded-full ${i <= currentStageIdx ? 'bg-primary' : 'bg-muted-foreground/20'}`} title={s.label} />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!readOnly && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowStageDialog(true)}>
                <ChevronRight className="h-3 w-3 mr-1" /> Advance Stage
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowStageConfig(true)}>
              <Settings className="h-3 w-3 mr-1" /> Stage Groups
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { loadVersions(); setShowVersions(true); }}>
              <History className="h-3 w-3 mr-1" /> Versions
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { loadComments(); setShowComments(!showComments); }}>
              <MessageSquare className="h-3 w-3 mr-1" /> Comments
              {comments.filter(c => !c.resolved).length > 0 && (
                <Badge className="ml-1 h-4 px-1 text-[9px] bg-red-500">{comments.filter(c => !c.resolved).length}</Badge>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b bg-muted/20">
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo" disabled={!editor.can().undo()}><Undo2 className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo" disabled={!editor.can().redo()}><Redo2 className="h-3.5 w-3.5" /></ToolbarButton>
          <Divider />
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><span className="text-xs font-bold">H1</span></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><span className="text-xs font-bold">H2</span></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><span className="text-xs font-bold">H3</span></ToolbarButton>
          <Divider />
          <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><Bold className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><Italic className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><UnderlineIcon className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Highlight"><Highlighter className="h-3.5 w-3.5" /></ToolbarButton>
          <Divider />
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Left"><AlignLeft className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centre"><AlignCenter className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Right"><AlignRight className="h-3.5 w-3.5" /></ToolbarButton>
          <Divider />
          <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullets"><List className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered"><ListOrdered className="h-3.5 w-3.5" /></ToolbarButton>
          <Divider />
          <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote"><Quote className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Code"><Code className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider"><Minus className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert Table"><TableIcon className="h-3.5 w-3.5" /></ToolbarButton>
          <Divider />
          <ToolbarButton onClick={setLinkFn} active={editor.isActive('link')} title="Link"><LinkIcon className="h-3.5 w-3.5" /></ToolbarButton>
          <ToolbarButton onClick={addImage} title="Image"><ImageIcon className="h-3.5 w-3.5" /></ToolbarButton>
          <Divider />
          <div className="relative" ref={varRef}>
            <ToolbarButton onClick={() => setShowVarPicker(!showVarPicker)} active={showVarPicker} title="Insert Variable">
              <span className="text-[10px] font-bold px-1">Variables</span>
            </ToolbarButton>
            {showVarPicker && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-card border rounded-lg shadow-xl w-64 max-h-72 overflow-y-auto py-1">
                <p className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase">Insert Variable</p>
                {CONTRACT_VARIABLES.map(v => (
                  <button key={v.var} onClick={() => insertVariable(v)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent flex items-center justify-between gap-2">
                    <span>{v.label}</span>
                    <code className="text-[9px] text-muted-foreground bg-muted px-1 rounded">{v.var}</code>
                  </button>
                ))}
              </div>
            )}
          </div>
          {contractNumber && (
            <ToolbarButton onClick={saveVersion} title="Save Version"><Save className="h-3.5 w-3.5" /></ToolbarButton>
          )}
          <Divider />
          {/* Track Changes status */}
          <div className={`flex items-center gap-1 ml-1 px-2 py-0.5 rounded border ${trackChangesActive ? 'bg-green-50 border-green-200' : 'bg-muted/40 border-border'}`}>
            <span className={`text-[10px] font-semibold ${trackChangesActive ? 'text-green-700' : 'text-muted-foreground'}`}>
              {trackChangesActive ? 'Track Changes ON' : 'Track Changes OFF (Draft)'}
            </span>
            {hasChanges && (
              <>
                <Badge className="text-[9px] h-4 px-1 bg-green-100 text-green-700 border-green-200">{changes.ins} ins</Badge>
                <Badge className="text-[9px] h-4 px-1 bg-red-100 text-red-700 border-red-200">{changes.del} del</Badge>
                <button onClick={acceptAllChanges} title="Accept All Changes" className="p-1 rounded hover:bg-green-100 text-green-600"><CheckCheck className="h-3.5 w-3.5" /></button>
                <button onClick={rejectAllChanges} title="Reject All Changes" className="p-1 rounded hover:bg-red-100 text-red-600"><XCircle className="h-3.5 w-3.5" /></button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Track changes color key */}
      {hasChanges && (
        <div className="flex items-center gap-3 px-3 py-1.5 bg-muted/30 border-b text-xs flex-wrap">
          <span className="font-semibold text-muted-foreground">Changes:</span>
          {Object.entries(changes.stages).map(([stageId, info]) => {
            const sc = getStageColor(stageId);
            const authors = [...info.authors].join(', ');
            return (
              <div key={stageId} className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border" style={{ borderColor: sc.border, background: sc.bg }}>
                <div className="w-2 h-2 rounded-full" style={{ background: sc.dot }} />
                <span style={{ color: sc.text }} className="font-medium">{sc.label}</span>
                <span style={{ color: sc.text }} className="opacity-70">({authors}) {info.ins > 0 ? `+${info.ins}` : ''}{info.del > 0 ? ` -${info.del}` : ''}</span>
              </div>
            );
          })}
          {readOnly && <span className="text-[10px] text-amber-700 ml-2">Changes must be accepted or rejected before execution.</span>}
        </div>
      )}

      {/* Main content area */}
      <div className="flex relative">
        <div className={`flex-1 ${showComments ? 'border-r' : ''}`}>
          <EditorContent editor={editor}
            className="prose prose-sm max-w-none min-h-[400px] p-5 focus-within:outline-none"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif", fontSize: '13px', lineHeight: '1.8' }}
          />
        </div>

        {/* Individual change accept/reject popup */}
        {changePopup && !readOnly && (
          <div className="fixed z-[100] bg-card border rounded-lg shadow-xl p-2.5 space-y-1.5"
            style={{ top: changePopup.top, left: changePopup.left, minWidth: 220, maxWidth: 320 }}>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-2 h-2 rounded-full" style={{ background: getStageColor(changePopup.stage).dot }} />
              <span className="font-semibold">{changePopup.isInsert ? 'Insertion' : 'Deletion'}</span>
              <span className="text-muted-foreground">by {changePopup.author}</span>
            </div>
            <p className="text-[11px] text-muted-foreground truncate italic">"{changePopup.text}"</p>
            <p className="text-[10px] text-muted-foreground">{changePopup.stageLabel}</p>
            <div className="flex gap-1.5 pt-1">
              <button onClick={acceptSingleChange}
                className="flex-1 px-2 py-1 rounded text-[11px] font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors">
                Accept
              </button>
              <button onClick={rejectSingleChange}
                className="flex-1 px-2 py-1 rounded text-[11px] font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors">
                Reject
              </button>
              <button onClick={() => setChangePopup(null)}
                className="px-2 py-1 rounded text-[11px] text-muted-foreground hover:bg-accent transition-colors">
                Close
              </button>
            </div>
          </div>
        )}

        {/* Comments sidebar */}
        {showComments && (
          <div className="w-72 bg-muted/10 flex flex-col max-h-[600px]">
            <div className="px-3 py-2 border-b flex items-center justify-between">
              <span className="text-xs font-semibold">Comments</span>
              <button onClick={() => setShowComments(false)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
            </div>
            {!readOnly && (
              <div className="p-2 border-b">
                <Textarea value={newComment} onChange={e => setNewComment(e.target.value)} rows={2} placeholder="Add a comment..." className="text-xs mb-1" />
                <Button size="sm" className="w-full h-7 text-xs" onClick={addComment} disabled={!newComment.trim()}>Add Comment</Button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {comments.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No comments yet</p>
              ) : comments.map(c => (
                <div key={c.comment_id} className={`p-2 rounded-lg border text-xs ${c.resolved ? 'bg-muted/30 opacity-60' : 'bg-card'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold">{c.author}</span>
                    {!c.resolved && !readOnly && (
                      <button onClick={() => resolveComment(c.comment_id)} className="text-green-600 hover:opacity-70" title="Resolve"><Check className="h-3 w-3" /></button>
                    )}
                  </div>
                  {c.selection_text && <p className="text-[10px] text-primary bg-primary/5 rounded px-1 py-0.5 mb-1 italic">"{c.selection_text}"</p>}
                  <p className="text-muted-foreground">{c.text}</p>
                  <p className="text-[9px] text-muted-foreground/60 mt-1">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</p>
                  {c.resolved && <Badge className="text-[8px] bg-green-100 text-green-700 mt-1">Resolved</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Version History Dialog */}
      {showVersions && (
        <Dialog open onOpenChange={() => { setShowVersions(false); setCompareVersion(null); }}>
          <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Version History</DialogTitle></DialogHeader>
            {compareVersion ? (
              <div className="flex-1 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => setCompareVersion(null)} className="text-sm text-primary hover:underline">Back to list</button>
                  <Badge variant="outline">Version {compareVersion.version} — {compareVersion.label}</Badge>
                </div>
                <div className="border rounded-lg p-4 prose prose-sm max-w-none" style={{ fontFamily: "'Georgia', serif", fontSize: '13px' }}
                  dangerouslySetInnerHTML={{ __html: compareVersion.body || '<p>Empty</p>' }} />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {versions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No versions saved yet. Click the Save icon in the toolbar to create a version.</p>
                ) : (
                  <div className="border rounded-lg divide-y">
                    {versions.map(v => (
                      <div key={v.version} className="flex items-center justify-between px-4 py-3 hover:bg-accent/20">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">v{v.version}</Badge>
                            <span className="text-sm font-medium">{v.label}</span>
                            <Badge className={`text-[9px] ${allStages.find(s => s.id === v.review_stage)?.color || ''}`}>
                              {allStages.find(s => s.id === v.review_stage)?.label || v.review_stage}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {v.saved_by} — {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                          </p>
                          {v.notes && <p className="text-xs text-amber-700 mt-0.5">{v.notes}</p>}
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                          const r = await api.get(`/api/contracts/${contractNumber}/versions/${v.version}`);
                          setCompareVersion(r.data);
                        }}><Eye className="h-3 w-3 mr-1" /> View</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Stage Change Dialog */}
      {showStageDialog && (
        <Dialog open onOpenChange={() => setShowStageDialog(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Change Review Stage</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mb-3">Current stage: <Badge className={`${currentStageDef.color}`}>{currentStageDef.label}</Badge></p>
            <div className="space-y-2 mb-3">
              {allStages.map(s => {
                const assigned = getStageGroup(s.id);
                const group = availableGroups.find(g => g.group_code === assigned?.group_code);
                return (
                  <button key={s.id} onClick={() => changeStage(s.id)}
                    disabled={s.id === reviewStage}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-all ${s.id === reviewStage ? 'border-primary bg-primary/5 font-semibold' : 'hover:border-primary/50 hover:bg-accent'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className={`${s.color} text-[10px] mr-2`}>{s.label}</Badge>
                        {s.id === reviewStage && <span className="text-xs text-primary">(current)</span>}
                      </div>
                      {group && (
                        <span className="text-[10px] text-muted-foreground">
                          {group.group_code} ({group.routing_method === 'all_at_once' ? 'All' : group.routing_method === 'sequential' ? 'Seq' : 'Any'}, {(group.members || []).length})
                        </span>
                      )}
                      {!group && s.id !== 'DRAFT' && s.id !== 'NEGOTIATION' && (
                        <span className="text-[10px] text-amber-600">No group assigned</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <Textarea value={stageNotes} onChange={e => setStageNotes(e.target.value)} rows={2} placeholder="Notes for this stage change (optional)" className="text-sm" />
          </DialogContent>
        </Dialog>
      )}

      {/* Stage Groups Configuration Dialog */}
      {showStageConfig && (
        <Dialog open onOpenChange={() => setShowStageConfig(false)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Configure Stage Approval Groups</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mb-4">Assign an approval group to each review stage.</p>
            <div className="space-y-3">
              {allStages.filter(s => s.id !== 'DRAFT' && s.id !== 'EXECUTED').map(stage => {
                const assigned = contractStageGroups[stage.id] || stageGroups[stage.id];
                return (
                  <div key={stage.id} className="flex items-center gap-3 p-3 rounded-lg border">
                    <Badge className={`${stage.color} text-[10px] w-36 justify-center flex-shrink-0`}>{stage.label}</Badge>
                    <select value={assigned?.group_code || ''}
                      onChange={e => { setContractStageGroups(prev => ({ ...prev, [stage.id]: e.target.value ? { group_code: e.target.value } : null })); }}
                      className="flex-1 h-8 rounded-lg border border-input bg-background px-2 text-sm min-w-0">
                      <option value="">No approval required</option>
                      {availableGroups.map(g => (
                        <option key={g.group_code} value={g.group_code}>
                          {g.group_code} — {g.routing_method === 'all_at_once' ? 'All Must Approve' : g.routing_method === 'sequential' ? 'Sequential' : 'Any One'} ({(g.members || []).length} members)
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => setShowStageConfig(false)}>Cancel</Button>
              <Button size="sm" onClick={async () => {
                try {
                  await api.put(`/api/contracts/${contractNumber}/review/stage-groups`, { stage_groups: contractStageGroups });
                  toast.success('Contract stage groups saved');
                  setShowStageConfig(false);
                } catch { toast.error('Save failed'); }
              }}>Save Configuration</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
